import { useState } from 'react'
import { Calendar, HardDrive, Trash2 } from 'lucide-react'

import { useIsPlatformOwner } from '../../../app/session'
import {
  Avatar,
  BarList,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  KeyValue,
  LoadingSkeleton,
  Meter,
  Money,
  PageHeader,
  QueryBoundary,
  Section,
  StatTile,
  StatusBadge,
  type Column,
} from '../../../components'
import { queryKeys, useUsage, type PlanCode, type UsageRow } from '../../../fixtures'
import { patch } from '../../../lib/apiClient'
import { formatBytes, formatGb, formatPercent, shareOf } from '../../../lib/format'
import { t } from '../../../lib/i18n'
import { useApiMutation } from '../../../lib/mutations'
import { useSuggestUpgradeBroadcast } from '../suggestUpgrade'

/**
 * P-07 Usage & storage.
 *
 * Usage is only interesting when it points at money, so every near-limit row
 * carries the upgrade AND what it is worth, and the storage panel names the one
 * lever that halves the biggest cost.
 *
 * That lever is irreversible. It is therefore PLATFORM_OWNER only, and absent —
 * not disabled — for a support account: a greyed-out control still announces a
 * capability the reader cannot have.
 *
 * SUGGEST UPGRADE — §6.1 P-07's "prefilled broadcast", and what it can be.
 *
 * `POST /platform/broadcast` addresses an AUDIENCE — `status`, `planCode`,
 * `nearLimit` — and cannot address one business. `{ nearLimit: true }` is
 * precisely this table, so the row's button seeds a message to every book near
 * a limit rather than to the row. `suggestUpgrade.tsx` carries the reasoning and
 * says it in the composer; nothing is sent on a single click, and under G22
 * nothing is delivered at all.
 */

/** Plan codes are stored; the label is looked up. A label is never stored. */
function planLabel(plan: PlanCode): string {
  switch (plan) {
    case 'TRIAL':
      return t('p.plan.TRIAL')
    case 'BASIC':
      return t('p.plan.BASIC')
    case 'TRADER':
      return t('p.plan.TRADER')
    case 'FARM':
      return t('p.plan.FARM')
  }
}

/** "21 of 25" with a meter, or "61 · unlimited" with none — a limitless plan has nothing to fill. */
function LimitCell({ used, limit }: { used: number; limit: number | null }) {
  if (limit === null) {
    return (
      <span className="whitespace-nowrap tnum">
        {t('p.usage.ofUnlimited', { used })}
      </span>
    )
  }

  return (
    <div className="min-w-[110px]">
      <span className="whitespace-nowrap tnum">{t('p.usage.ofLimit', { used, limit })}</span>
      <Meter value={used} max={limit} />
    </div>
  )
}

export function UsageScreen() {
  const usage = useUsage()
  const isOwner = useIsPlatformOwner()

  const [leverOpen, setLeverOpen] = useState(false)

  /*
    P-07's "Suggest upgrade", as plan.md §6.1 asks: a PREFILLED BROADCAST.

    `{ nearLimit: true }` is exactly this table's population, which is the one
    place in the whole console where the broadcast's audience model and a
    screen's contents line up. The composer opens seeded; the human edits it and
    presses the button. Nothing is sent on a single click.

    The count passed in is the same §4.17 figure the server resolves the
    audience with, so the button's "Queue for N books" is not the console's own
    arithmetic.
  */
  const suggest = useSuggestUpgradeBroadcast(usage.data?.nearLimitCount)

  /*
    THE LEVER — one field of `PATCH /platform/settings`, and nothing more.

    It is marked irreversible in the design and U1 is still an open question
    with the client, so the confirm stays exactly as it is: type-to-confirm,
    platform owner only, absent for support. What CANNOT be claimed is a
    deletion. Nothing on the server removes an original today — the flag records
    a policy for what is kept, and that is what the toast says.
  */
  const dropOriginals = useApiMutation<void, unknown>({
    run: async () => patch('/platform/settings', { keepOriginalVideos: false }),
    invalidates: [queryKeys.usage, queryKeys.consoleSettings, queryKeys.platformOverview],
    success: t('p.usage.leverDone'),
  })

  const columns: Column<UsageRow>[] = [
    {
      id: 'business',
      header: t('label.business'),
      sortBy: (row) => row.name,
      cell: (row) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={row.name} />
          <b className="font-display font-bold text-dark">{row.name}</b>
        </div>
      ),
    },
    {
      id: 'plan',
      header: t('label.plan'),
      cell: (row) => <StatusBadge>{planLabel(row.plan)}</StatusBadge>,
    },
    {
      id: 'animals',
      header: t('label.animals'),
      sortBy: (row) => row.animals,
      cell: (row) => <LimitCell used={row.animals} limit={row.animalLimit} />,
    },
    {
      id: 'people',
      header: t('label.people'),
      sortBy: (row) => row.people,
      cell: (row) => <LimitCell used={row.people} limit={row.peopleLimit} />,
    },
    {
      id: 'storage',
      header: t('label.storage'),
      sortBy: (row) => row.storageMb,
      cell: (row) => (
        <div className="min-w-[110px]">
          <span className="whitespace-nowrap tnum">
            {t('p.usage.ofLimit', {
              used: formatBytes(row.storageMb),
              limit: formatGb(row.storageLimitGb),
            })}
          </span>
          <Meter
            value={row.storageMb}
            max={row.storageLimitGb === null ? null : row.storageLimitGb * 1024}
          />
        </div>
      ),
    },
    {
      id: 'suggest',
      header: t('p.usage.colSuggest'),
      numeric: true,
      cell: (row) => {
        if (!row.suggest) return <StatusBadge>{t('p.usage.roomToSpare')}</StatusBadge>

        const offer = (
          <span className="inline-flex items-center gap-1">
            {planLabel(row.suggest.plan)}
            <span aria-hidden="true">·</span>
            <Money paisa={row.suggest.upliftPaisa} signed />
          </span>
        )

        /*
          §6.1 P-07 says "both", but `POST /platform/broadcast` is
          `platform.settings` and the API gives a support actor a 403 with
          `missing: ['platform.settings']`. This console gates by ABSENCE, so
          support sees the offer as a fact — which plan, worth how much — and
          not a button that could only ever be refused. Logged as C5b.
        */
        const suggestRow = row.suggest
        return isOwner ? (
          <Button
            size="sm"
            onClick={() => suggest.open({ name: row.name, plan: planLabel(suggestRow.plan) })}
          >
            {offer}
          </Button>
        ) : (
          <StatusBadge tone="accent">{offer}</StatusBadge>
        )
      },
    },
  ]

  return (
    <>
      <PageHeader
        title={t('p.usage.title')}
        subtitle={t('p.usage.subtitle')}
        actions={<Button icon={<Calendar className="size-4" />}>{t('p.overview.thisMonth')}</Button>}
      />

      <QueryBoundary
        query={usage}
        skeleton={
          <>
            <LoadingSkeleton variant="tiles" />
            <LoadingSkeleton variant="table" rows={4} className="mt-3.5" />
          </>
        }
      >
        {(data) => (
          <>
            <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
              <StatTile
                label={t('p.usage.storageUsed')}
                value={formatGb(data.storageUsedGb)}
                sub={
                  <>
                    {t('p.usage.storageUsedSub', {
                      total: formatGb(data.storageLimitGb),
                      percent: formatPercent(shareOf(data.storageUsedGb, data.storageLimitGb)),
                    })}
                    <Meter value={data.storageUsedGb} max={data.storageLimitGb} />
                  </>
                }
              />
              <StatTile
                label={t('p.usage.growingBy')}
                value={
                  <>
                    {formatGb(data.growthGbPerMonth)}
                    <small className="text-[15px] font-bold">{t('unit.perMonthShort')}</small>
                  </>
                }
                sub={t('p.usage.growingSub')}
              />
              <StatTile
                label={t('p.usage.costsYou')}
                value={
                  <>
                    <Money paisa={data.hostingCostPaisa} />
                    <small className="text-[15px] font-bold">{t('unit.perMonthShort')}</small>
                  </>
                }
                sub={t('p.usage.costsYouSub', { share: formatPercent(data.hostingShare, 1) })}
              />
              <StatTile
                label={t('p.usage.nearLimit')}
                value={<span className="text-warn">{data.nearLimitCount}</span>}
                sub={t('p.usage.nearLimitSub')}
              />
            </div>

            <Section
              className="mt-3.5"
              title={t('p.usage.closeToLimits')}
              subtitle={t('p.usage.closeToLimitsSub')}
            >
              <DataTable
                columns={columns}
                rows={data.rows}
                rowKey={(row) => row.slug}
                caption={t('p.usage.closeToLimits')}
                empty={
                  <EmptyState
                    icon={<HardDrive className="size-6" />}
                    title={t('p.usage.emptyTitle')}
                    body={t('p.usage.emptyBody')}
                  />
                }
              />
            </Section>

            <div className="mt-3.5 grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
              <Section title={t('p.usage.whereStorageGoes')}>
                <BarList
                  items={data.breakdown.map((slice) => ({
                    id: slice.label,
                    label: slice.label,
                    // `value` sizes the bar and stays a number; `display` is
                    // read by a person and goes through the formatter, which is
                    // what this line did not do — a gigabyte ratio interpolated
                    // straight into "{value} GB" printed sixteen decimals of it.
                    value: slice.gb,
                    display: formatGb(slice.gb),
                  }))}
                />
                <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-xs leading-relaxed text-muted">
                  {t('p.usage.storageNote')}
                </p>
              </Section>

              <Section surface="accent" title={t('p.usage.lever')}>
                <KeyValue
                  label={t('p.usage.originalsKept')}
                  value={formatGb(data.lever.originalsGb)}
                />
                <KeyValue
                  label={t('p.usage.ifDropped')}
                  value={
                    <span className="text-success">
                      {t('p.usage.savedGb', { value: formatGb(data.lever.savedGb) })}
                    </span>
                  }
                />
                <KeyValue
                  label={t('p.usage.billAfter')}
                  value={
                    <>
                      <Money paisa={data.lever.billAfterPaisa} tone="positive" />
                      <small className="text-success">{t('unit.perMonthShort')}</small>
                    </>
                  }
                />
                <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-xs leading-relaxed text-muted">
                  {t('p.usage.leverNote')}
                </p>

                {/* U1. Irreversible, so it is the platform owner's alone — and
                    absent, not disabled, for everybody else. */}
                {isOwner ? (
                  <Button
                    variant="danger"
                    className="mt-3.5 w-full"
                    icon={<Trash2 className="size-4" />}
                    disabled={dropOriginals.isPending}
                    onClick={() => setLeverOpen(true)}
                  >
                    {t('p.usage.dropOriginals')}
                  </Button>
                ) : null}
              </Section>
            </div>

            {suggest.element}

            <ConfirmDialog
              open={leverOpen}
              onClose={() => setLeverOpen(false)}
              onConfirm={() => dropOriginals.mutate()}
              disabled={dropOriginals.isPending}
              tone="danger"
              title={t('p.usage.leverConfirmTitle')}
              body={t('p.usage.leverConfirmBody')}
              consequence={t('confirm.irreversible')}
              typeToConfirm={t('p.usage.leverConfirmPhrase')}
              confirmLabel={t('p.usage.dropOriginals')}
            />
          </>
        )}
      </QueryBoundary>
    </>
  )
}
